import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Message, MessageFolder } from "@asd/customer-portal-api";

import { MessagesService } from "../services";

type MessagesState = {
  [MessageFolder.Inbox]: Message[];
  [MessageFolder.Favorites]: Message[];
  [MessageFolder.Archive]: Message[];
};

type MessagesContextType = {
  messages: MessagesState;
  totalUnread: number;
  isLoading: boolean;
  hasError: boolean;
  moveMessages: (messageIds: string[], folder: MessageFolder) => Promise<void>;
  updateMessagesReadStatus: (messageIds: string[], isRead: boolean) => Promise<void>;
};

const MessagesContext = createContext<MessagesContextType>({} as MessagesContextType);

export const useMessages = () => useContext(MessagesContext);

const isAbortError = (error: any) => error.name === "AbortError" || error.code === 20;

const MessagesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [messages, setMessages] = useState<MessagesState>({
    [MessageFolder.Inbox]: [],
    [MessageFolder.Favorites]: [],
    [MessageFolder.Archive]: [],
  });

  const getMessagesAbortControllerRef = useRef<AbortController | null>(null);
  const moveMessagesAbortControllerRef = useRef<AbortController | null>(null);
  const updateMessagesReadStatusAbortControllerRef = useRef<AbortController | null>(null);

  const totalUnread = useMemo(
    () =>
      Object.values(messages)
        .flat()
        .filter((m) => !m.isRead).length,
    [messages],
  );

  const getMessages = useCallback(async () => {
    if (getMessagesAbortControllerRef.current) {
      getMessagesAbortControllerRef.current.abort();
    }

    getMessagesAbortControllerRef.current = new AbortController();

    setIsLoading(true);
    setHasError(false);

    try {
      const response = await MessagesService.getMessages(undefined, undefined, undefined, {
        signal: getMessagesAbortControllerRef.current?.signal,
      });

      const data = response?.data || [];

      setMessages({
        [MessageFolder.Inbox]: data.filter((message: Message) => message.folder === MessageFolder.Inbox),
        [MessageFolder.Favorites]: data.filter((message: Message) => message.folder === MessageFolder.Favorites),
        [MessageFolder.Archive]: data.filter((message: Message) => message.folder === MessageFolder.Archive),
      });
    } catch (error: any) {
      if (!isAbortError(error)) {
        setHasError(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const moveMessages = useCallback(
    async (messageIds: string[], folder: MessageFolder) => {
      if (moveMessagesAbortControllerRef.current) {
        moveMessagesAbortControllerRef.current.abort();
      }

      moveMessagesAbortControllerRef.current = new AbortController();

      setHasError(false);

      try {
        const response = await MessagesService.moveMessages(
          { messageIds, folder },
          {
            signal: moveMessagesAbortControllerRef.current?.signal,
          },
        );
        if (response?.status === 204) {
          await getMessages();
        }
      } catch (error: any) {
        if (!isAbortError(error)) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    },
    [getMessages],
  );

  const updateMessagesReadStatus = useCallback(
    async (messageIds: string[], isRead: boolean) => {
      if (updateMessagesReadStatusAbortControllerRef.current) {
        updateMessagesReadStatusAbortControllerRef.current.abort();
      }

      updateMessagesReadStatusAbortControllerRef.current = new AbortController();

      setHasError(false);

      try {
        const response = await MessagesService.updateMessagesReadStatus(
          { messageIds, isRead },
          {
            signal: updateMessagesReadStatusAbortControllerRef.current?.signal,
          },
        );
        if (response?.status === 204) {
          await getMessages();
        }
      } catch (error: any) {
        if (!isAbortError(error)) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    },
    [getMessages],
  );

  useEffect(() => {
    getMessages();

    return () => {
      getMessagesAbortControllerRef.current?.abort();
      moveMessagesAbortControllerRef.current?.abort();
      updateMessagesReadStatusAbortControllerRef.current?.abort();
    };
  }, [getMessages]);

  return (
    <MessagesContext.Provider value={{ messages, totalUnread, isLoading, hasError, moveMessages, updateMessagesReadStatus }}>
      {children}
    </MessagesContext.Provider>
  );
};

export default MessagesProvider;
